import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, useBreakpointValue, useStyleConfig} from '@chakra-ui/react'
import {Controller, Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {ChevronRight, ChevronLeft} from '../../icons/arrows'
import {remCalc} from '../../../theme/_utils'
import {calculateVerticalPadding} from '../utils'
import {ContentSliderTile} from './content-slider-tile'

export const ContentSlider = ({
    variant = "Blue De'Longhi",
    hasPaddings = true,
    maxWidth = remCalc(1920),
    contentSliderGroup = [],
}) => {
    const isMobile = useBreakpointValue({base: true, md: false})
    const verticalPadding = calculateVerticalPadding('', hasPaddings)

    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('ContentSlider')

    let backgroundColor = 'background.brand'
    let ctaVariant = 'inverse-primary'
    let textVariant = 'text.inverse'
    switch (variant) {
        case 'Black':
            backgroundColor = 'background.dark'
            break
        case 'Light Grey':
            backgroundColor = 'background.grey'
            ctaVariant = 'secondary'
            textVariant = 'text.default'
            break
    }

    return (
        <Box
            data-testid="content-slider"
            my={verticalPadding}
            px={{base: remCalc(16), md: remCalc(24), xl: 0}}
            pt={{base: remCalc(16), xl: 0}}
            pb={{base: remCalc(40), xl: 0}}
            maxW={maxWidth}
            backgroundColor={backgroundColor}
            {...styles.container}
        >
            <Box __css={{...swiperStyles, ...styles.content}} display={{base: 'block', xl: 'none'}}>
                {contentSliderGroup && contentSliderGroup?.length > 0 && (
                    <Swiper
                        className={'cs-swiper-mobile'}
                        key={{base: 'mobile-view', md: 'other-view-small'}}
                        modules={[Controller, Keyboard, Pagination]}
                        centeredSlides={true}
                        slidesPerView={1}
                        pagination={{
                            clickable: true,
                            el: '.cs-mobile-pagination',
                            renderBullet: (index, className) =>
                                `<span class="${className}" id="pag-tab-${index}">${contentSliderGroup[index].textblock?.label}</span>`,
                        }}
                        onSlideChangeTransitionStart={(swiper) => {
                            if (isMobile) {
                                document.getElementById('mobile-pagination-container').scrollLeft =
                                    document.getElementById(
                                        `pag-tab-${swiper.activeIndex}`,
                                    ).offsetLeft
                            }
                        }}
                    >
                        {contentSliderGroup.map((tile, tIndex) => (
                            <SwiperSlide key={`content-slider-tile-${tIndex}`} {...styles.slide}>
                                <ContentSliderTile
                                    image={tile?.image}
                                    textblock={tile?.textblock}
                                    textVariant={textVariant}
                                    ctaVariant={ctaVariant}
                                />
                            </SwiperSlide>
                        ))}
                        <Box id="mobile-pagination-container">
                            <Flex
                                className="cs-mobile-pagination"
                                {...styles.mobilePagination}
                                color={textVariant}
                            />
                        </Box>
                    </Swiper>
                )}
            </Box>
            <Box __css={{...swiperStyles, ...styles.content}} display={{base: 'none', xl: 'block'}}>
                {contentSliderGroup && contentSliderGroup?.length > 0 && (
                    <Swiper
                        className={'cs-swiper-desktop'}
                        key={'other-view-desktop'}
                        modules={[Controller, Navigation, Pagination, Keyboard]}
                        pagination={{
                            clickable: true,
                            el: '.cs-pagination',
                            renderBullet: (index, className) =>
                                `<span class="${className}">${contentSliderGroup[index].textblock?.label}</span>`,
                        }}
                        navigation={{
                            prevEl: `.cs-swiper-button-prev`,
                            nextEl: `.cs-swiper-button-next`,
                        }}
                        keyboard={{
                            enabled: true,
                        }}
                        loop={true}
                        centeredSlides={true}
                        slidesPerView={1.2}
                        maxBackfaceHiddenSlides={0}
                    >
                        {contentSliderGroup.map((tile, tIndex) => (
                            <SwiperSlide key={`content-slider-tile-${tIndex}`} {...styles.slide}>
                                <ContentSliderTile
                                    image={tile?.image}
                                    textblock={tile?.textblock}
                                    textVariant={textVariant}
                                    ctaVariant={ctaVariant}
                                />
                            </SwiperSlide>
                        ))}
                        <ChevronLeft
                            className="cs-swiper-button-prev"
                            aria-label="swiper button prev icon"
                            {...styles.chevronIcon}
                        />
                        <ChevronRight
                            className="cs-swiper-button-next"
                            aria-label="swiper button next icon"
                            {...styles.chevronIcon}
                        />

                        <Flex
                            className="cs-pagination"
                            {...styles.pagination}
                            color="text.inverse"
                        />
                    </Swiper>
                )}
            </Box>
        </Box>
    )
}

ContentSlider.propTypes = {
    hasPaddings: PropTypes.bool,
    variant: PropTypes.string,
    contentSliderGroup: PropTypes.arrayOf(
        PropTypes.shape({
            image: PropTypes.shape(),
            textblock: PropTypes.shape({
                label: PropTypes.string,
                label_paragraph: PropTypes.string,
                title: PropTypes.string,
                body_copy: PropTypes.string,
                cta: PropTypes.shape({title: PropTypes.string, href: PropTypes.string}),
            }),
        }),
    ),
    maxWidth: PropTypes.string,
}
